import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { browseExportTemplate } from '../services/export.service';
import type { ExportTemplateBrowseRequest } from '../types/export.types';

/** Rows per page in the full-run view. The server bounds the field to 1–200. */
export const BROWSE_PAGE_SIZE = 100;

interface BrowseArgs {
  request: ExportTemplateBrowseRequest;
  /** False while the run or tenant is unresolved — the request reads the
   *  enrichment table, so it must not fire against a half-built body. */
  enabled: boolean;
}

/**
 * One page of a run's products for the full-page export view.
 *
 * A query rather than a mutation, keyed on the cursor and the search as well as
 * the render inputs: paging back to a page already seen is then a cache hit,
 * which is what makes Prev feel instant. `staleTime: Infinity` for the same
 * reason the preview uses it — the enrichment rows are immutable once a run has
 * completed, so re-reading them within a session cannot change the answer.
 *
 * `placeholderData` keeps the previous page's rows on screen while the next one
 * loads, so paging doesn't blank the table and collapse its height.
 */
export function useExportBrowse({ request, enabled }: BrowseArgs) {
  const { sourceWorkflowId, template, fields, filters, pageSize, cursor, search, columnFilters } =
    request;

  return useQuery({
    queryKey: queryKeys.exports.templateBrowse(
      sourceWorkflowId,
      template ?? null,
      fields ?? null,
      filters ?? null,
      pageSize ?? BROWSE_PAGE_SIZE,
      cursor ?? null,
      search ?? null,
      columnFilters ?? null,
    ),
    queryFn: () => browseExportTemplate({ ...request, pageSize: pageSize ?? BROWSE_PAGE_SIZE }),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previous) => previous,
  });
}
